import type { WindowLocation } from '@reach/router';
import React, { useRef } from 'react';

import { NavTab } from 'src/components/layout/club-dropdown-header-nav';
import { SiteContext } from 'src/context/site-context';
import useOutsideClick from 'src/custom-hooks/use-outside-click';
import { useToggle } from 'src/custom-hooks/use-toggle';
import { isChildOfClubPage } from 'src/helpers/path-matching-helper';
import { shadow, zIndexes } from 'src/styling/constants';
import { translateLinkIntoLanguage } from 'src/translators/links-translator';
import type { StaticTextLookupKey } from 'src/translators/static-text-lookup';
import { TranslatedStaticText } from 'src/translators/static-text-translator';

import { WebsiteLink } from '../common/website-link';
import FacilitySelect from '../facilities/facility-select';

import type { ClubPageTypeProps } from './club-page-type';
import { clubPageTypeHeaderContent } from './club-page-type';
import { HeaderSubmenuSelect } from './header-submenu-select';

type ClubDropdownHeaderProps = {
  location: WindowLocation;
  clubPageType?: ClubPageTypeProps;
  displayText: string;
};

const ClubDropdownHeader: React.FC<ClubDropdownHeaderProps> = ({
  clubPageType,
  location,
  displayText,
}) => {
  const { siteId, styling, language, isDL } = React.use(SiteContext);
  const ref = useRef<HTMLDivElement | null>(null);
  const [collapsed, setCollapsed, collapse] = useToggle(true);

  const toggleCollapsed = () => setCollapsed(!collapsed);

  useOutsideClick(ref, collapse);

  const onFinishMenuAction = () => collapse();

  const { childOfClubPageLink, seeAllTitleLookupKey, seeAllLink } =
    clubPageTypeHeaderContent(clubPageType);
  const shouldHighlight = isChildOfClubPage(location, siteId, childOfClubPageLink);

  return (
    <div
      css={{
        padding: isDL ? 'unset' : '1rem',
        margin: isDL ? '0.875rem 1rem' : 'unset',
      }}
      ref={ref}
    >
      <NavTab
        isCollapsed={!!collapsed}
        toggleCollapsed={toggleCollapsed}
        displayText={displayText}
        shouldHighlight={shouldHighlight}
      />
      {collapsed ? null : (
        <div
          css={[
            shadow.headerBelow,
            {
              left: 0,
              right: 0,
              zIndex: zIndexes.clubFacilitiesHeader,
              position: 'absolute',
              backgroundColor: styling.colors.headerBackground,
              paddingBottom: '1rem',
              top: '100%',
              borderTop: isDL ? `1px solid ${styling.colors.headerBorder}` : undefined,
            },
          ]}
        >
          <div css={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            {clubPageType !== undefined ? (
              <HeaderSubmenuSelect type={clubPageType} onClick={onFinishMenuAction} />
            ) : (
              <FacilitySelect
                center={false}
                useSmallThumbnails
                gtmTrackingId="nav-tab-facility-link"
                onClick={onFinishMenuAction}
                isNav
              />
            )}
          </div>
          <WebsiteLink
            to={translateLinkIntoLanguage(seeAllLink, language)}
            handleClick={onFinishMenuAction}
          >
            <div
              css={[
                styling.common.link,
                { textAlign: 'center', display: 'inline-block', margin: '1rem 0' },
                isDL ? { color: styling.colors.capeCod } : {},
              ]}
              data-gtmid="nav-tab-see-all-facilities"
            >
              {<TranslatedStaticText lookupKey={seeAllTitleLookupKey as StaticTextLookupKey} />}
            </div>
          </WebsiteLink>
        </div>
      )}
    </div>
  );
};

export default ClubDropdownHeader;
